import { useQuery } from '@tanstack/react-query';
import fetchProfitPoolData from 'components/pages/ProfitPoolOverview/apiUtils';
import { FilterContextData } from 'contexts/FilterContext';
import { useContext, useEffect, useState } from 'react';
import { StrategicOpportunityKpis } from '../StrategicOpportunity';

const useStrategicOpportunityData = (
    selectedStrategicOpportunityKpis: StrategicOpportunityKpis,
    selectedSubFilter: {
        dataKey: string;
        dimension: string;
        isAttribute: boolean;
    },
    selectedColoredBy: {
        key: string;
        dimension: string;
        isAttribute: boolean;
    },
) => {
    const [transformedData, setTransformedData] = useState<any[]>([]);

    const { timePeriodSelection, idForRestOfComponent, allDimAndAttributeTypeList, timePeriodLoading } =
        useContext(FilterContextData);

    const {
        data: xAxisData,
        isPending: isXAxisPending,
        isError: isXAxisError,
        isSuccess: isXAxisSuccess,
    } = useQuery({
        queryKey: [
            'strategic-opportunity-data',
            selectedStrategicOpportunityKpis.xaxis,
            selectedSubFilter,
            timePeriodSelection,
            idForRestOfComponent,
            selectedColoredBy,
        ],
        queryFn: ({ signal }) => {
            return fetchProfitPoolData({
                selectedSubFilter,
                kpi: selectedStrategicOpportunityKpis.xaxis?.kpi,
                tierList: selectedStrategicOpportunityKpis.xaxis?.tier,
                yearAgo: false,
                groupedByList: ['attribute'],
                dimIdsFilter: idForRestOfComponent,
                frequency: 'M',
                timePeriod: JSON.parse(timePeriodSelection),
                signal,
                parentDimension: selectedColoredBy.dimension,
                parentAttributeType: selectedColoredBy.key,
            });
        },
        enabled: Boolean(
            allDimAndAttributeTypeList &&
                'loading' in allDimAndAttributeTypeList &&
                !timePeriodLoading &&
                timePeriodSelection,
        ),
    });

    const {
        data: yAxisData,
        isPending: isYAxisPending,
        isError: isYAxisError,
        isSuccess: isYAxisSuccess,
    } = useQuery({
        queryKey: [
            'strategic-opportunity-data',
            selectedStrategicOpportunityKpis.yaxis,
            selectedSubFilter,
            timePeriodSelection,
            idForRestOfComponent,
        ],
        queryFn: ({ signal }) => {
            return fetchProfitPoolData({
                selectedSubFilter,
                kpi: selectedStrategicOpportunityKpis.yaxis?.kpi,
                tierList: selectedStrategicOpportunityKpis.yaxis?.tier,
                yearAgo: false,
                groupedByList: ['attribute'],
                dimIdsFilter: idForRestOfComponent,
                frequency: 'M',
                timePeriod: JSON.parse(timePeriodSelection),
                signal,
            });
        },
        enabled: Boolean(
            allDimAndAttributeTypeList &&
                'loading' in allDimAndAttributeTypeList &&
                !timePeriodLoading &&
                timePeriodSelection,
        ),
    });

    const {
        data: bubbleSizeData,
        isPending: isBubbleSizePending,
        isError: isBubbleSizeError,
        isSuccess: isBubbleSizeSuccess,
    } = useQuery({
        queryKey: [
            'strategic-opportunity-data',
            selectedStrategicOpportunityKpis.bubbleSize,
            selectedSubFilter,
            timePeriodSelection,
            idForRestOfComponent,
        ],
        queryFn: ({ signal }) => {
            return fetchProfitPoolData({
                selectedSubFilter,
                kpi: selectedStrategicOpportunityKpis.bubbleSize?.kpi,
                tierList: selectedStrategicOpportunityKpis.bubbleSize?.tier,
                yearAgo: false,
                groupedByList: ['attribute'],
                dimIdsFilter: idForRestOfComponent,
                frequency: 'M',
                timePeriod: JSON.parse(timePeriodSelection),
                signal,
            });
        },
        enabled: Boolean(
            allDimAndAttributeTypeList &&
                'loading' in allDimAndAttributeTypeList &&
                !timePeriodLoading &&
                timePeriodSelection,
        ),
    });

    useEffect(() => {
        // Only run data manipulation when all data is defined and has data arrays
        if (
            xAxisData?.data &&
            Array.isArray(xAxisData.data) &&
            yAxisData?.data &&
            Array.isArray(yAxisData.data) &&
            bubbleSizeData?.data &&
            Array.isArray(bubbleSizeData.data)
        ) {
            // Create a map to group data by attribute_desc
            const dataMap = new Map<
                string,
                {
                    yFieldValue?: number;
                    xFieldValue?: number;
                    bubbleSizeValue?: number;
                    attribute_desc: string;
                    attribute_ex_id?: string;
                    parentAttributeDesc?: string;
                }
            >();

            // Helper function to map KPI name from request to response key
            const getKpiResponseKey = (kpi: string | undefined): string => {
                if (!kpi) return '';
                // API request uses 'margin_percent' but response has 'margin_percentage'
                if (kpi === 'margin_percent') {
                    return 'margin_percentage';
                }
                return kpi;
            };

            // Process xAxisData (yFieldValue values)
            xAxisData.data.forEach((item: any) => {
                const attributeDesc = item.attribute_desc;
                if (attributeDesc) {
                    if (!dataMap.has(attributeDesc)) {
                        dataMap.set(attributeDesc, {
                            attribute_desc: attributeDesc,
                            attribute_ex_id: item.attribute_ex_id,
                        });
                    }
                    const entry = dataMap.get(attributeDesc)!;
                    const responseKpiKey = getKpiResponseKey(selectedStrategicOpportunityKpis.xaxis?.kpi);
                    entry.xFieldValue = item.kpi_values[responseKpiKey];
                    entry.parentAttributeDesc = item.parent_attribute_desc;
                    if (item.attribute_ex_id && !entry.attribute_ex_id) {
                        entry.attribute_ex_id = item.attribute_ex_id;
                    }
                }
            });

            // Process yAxisData (xFieldValue values)
            yAxisData.data.forEach((item: any) => {
                const attributeDesc = item.attribute_desc;
                if (attributeDesc) {
                    if (!dataMap.has(attributeDesc)) {
                        dataMap.set(attributeDesc, { attribute_desc: attributeDesc });
                    }
                    const entry = dataMap.get(attributeDesc)!;
                    const responseKpiKey = getKpiResponseKey(selectedStrategicOpportunityKpis.yaxis?.kpi);
                    entry.yFieldValue = item.kpi_values[responseKpiKey];
                }
            });

            // Process bubbleSizeData (bubbleSizeValue/volume_sales_equivalent values)
            bubbleSizeData.data.forEach((item: any) => {
                const attributeDesc = item.attribute_desc;
                if (attributeDesc) {
                    if (!dataMap.has(attributeDesc)) {
                        dataMap.set(attributeDesc, { attribute_desc: attributeDesc });
                    }
                    const entry = dataMap.get(attributeDesc)!;
                    // Use volume_sales_equivalent if available, otherwise use value
                    entry.bubbleSizeValue = item.kpi_values[selectedStrategicOpportunityKpis.bubbleSize?.kpi ?? ''];
                }
            });

            // Transform to reducedOpprData format
            const transformed = Array.from(dataMap.values()).map((item) => ({
                tactics: item.parentAttributeDesc ?? item.attribute_desc,
                name: item.attribute_desc,
                attribute_ex_id: item.attribute_ex_id ?? '',
                yFieldValue: item.yFieldValue ?? null,
                xFieldValue: item.xFieldValue ?? null,
                bubbleSizeValue: item.bubbleSizeValue ?? null,
                parentAttributeDesc: item.parentAttributeDesc ?? null,
            }));

            setTransformedData(transformed);
        } else {
            setTransformedData([]);
        }
    }, [xAxisData, yAxisData, bubbleSizeData, selectedStrategicOpportunityKpis]);

    return {
        xAxisData,
        yAxisData,
        bubbleSizeData,
        transformedData,
        isPending: isXAxisPending || isYAxisPending || isBubbleSizePending,
        isError: isXAxisError || isYAxisError || isBubbleSizeError,
        isSuccess: isXAxisSuccess || isYAxisSuccess || isBubbleSizeSuccess,
    };
};

export default useStrategicOpportunityData;
